import React from 'react';
import styled from 'styled-components';

const ConfirmationContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
  background: linear-gradient(180deg, #ffffff, #fafafa);
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
`;

const SuccessIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #2ecc71, #27ae60);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 3rem;
  font-weight: bold;
  box-shadow: 0 8px 24px rgba(46, 204, 113, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    border: 2px solid rgba(46, 204, 113, 0.2);
    animation: pulse 2s ease-out infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }
`;

const SuccessTitle = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  font-weight: 700;
`;

const SuccessMessage = styled.p`
  font-size: 1.15rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2.5rem;
  line-height: 1.7;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const BookingReference = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
  color: white;
  padding: 2rem;
  border-radius: 16px;
  margin-bottom: 3rem;
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-10%, -10%);
    }
  }
`;

const ReferenceNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-top: 0.75rem;
  letter-spacing: 3px;
  font-family: 'Courier New', monospace;
`;

const DetailsSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: left;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  
  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: ${props => props.theme.colors.accent};
    border-radius: 2px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const TicketsList = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const TicketItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
  }
`;

const TicketInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TicketName = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const TicketQuantity = styled.span`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
`;

const TicketPrice = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  font-size: 1.1rem;
`;

const TotalSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  border: 2px solid ${props => props.theme.colors.accent};
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
    border-radius: 12px 12px 0 0;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.2rem;
    color: ${props => props.theme.colors.primary};
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid ${props => props.theme.colors.accent};
  }
`;

const TotalLabel = styled.span`
  color: ${props => props.theme.colors.secondary};
`;

const TotalValue = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: ${props => props.theme.colors.primary};
  color: white;
  border-color: ${props => props.theme.colors.primary};
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  font-size: 1.1rem;
  padding: 1.25rem 3rem;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  }
`;

const InfoBox = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2.5rem;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
    border-radius: 12px;
    z-index: -1;
    opacity: 0.1;
  }
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  font-weight: 500;
`;

const BookingConfirmation = ({ 
  bookingReference, 
  selectedTickets, 
  selectedDate, 
  selectedTimeSlot, 
  customerDetails, 
  totalAmount 
}) => {
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    return timeSlot?.time || timeSlot || 'Not specified';
  };

  const handleReturnHome = () => {
    window.location.href = '/';
  };

  const vatAmount = totalAmount * 0.2;
  const finalTotal = totalAmount + vatAmount;

  return (
    <ConfirmationContainer>
      <SuccessIcon>
        ✓
      </SuccessIcon>
      
      <SuccessTitle>Booking Confirmed!</SuccessTitle>
      <SuccessMessage>
        Thank you for your booking. Your visit to Belvoir Castle has been confirmed.
        A confirmation email has been sent to {customerDetails.email}.
      </SuccessMessage>

      <BookingReference>
        <div>Booking Reference</div>
        <ReferenceNumber>{bookingReference}</ReferenceNumber>
      </BookingReference>

      <DetailsSection>
        <SectionTitle>
          Visit Details
        </SectionTitle>
        
        <DetailsGrid>
          <DetailItem>
            <DetailLabel>Visit Date</DetailLabel>
            <DetailValue>{formatDate(selectedDate)}</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Visit Time</DetailLabel>
            <DetailValue>{formatTime(selectedTimeSlot)}</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Total Tickets</DetailLabel>
            <DetailValue>
              {selectedTickets.reduce((total, ticket) => total + ticket.quantity, 0)}
            </DetailValue>
          </DetailItem>
        </DetailsGrid>
      </DetailsSection>

      <DetailsSection>
        <SectionTitle>
          Contact Information
        </SectionTitle>
        
        <DetailsGrid>
          <DetailItem>
            <DetailLabel>Name</DetailLabel>
            <DetailValue>{customerDetails.firstName} {customerDetails.lastName}</DetailValue>
          </DetailItem>
          
          <DetailItem>
            <DetailLabel>Email</DetailLabel>
            <DetailValue>{customerDetails.email}</DetailValue>
          </DetailItem>
          
          {customerDetails.phone && (
            <DetailItem>
              <DetailLabel>Phone</DetailLabel>
              <DetailValue>{customerDetails.phone}</DetailValue>
            </DetailItem>
          )}
        </DetailsGrid>
      </DetailsSection>

      <DetailsSection>
        <SectionTitle>
          Tickets
        </SectionTitle>
        
        <TicketsList>
          {selectedTickets
            .filter(ticket => ticket.quantity > 0)
            .map(ticket => (
              <TicketItem key={ticket.webSaleItemId}>
                <TicketInfo>
                  <TicketName>{ticket.name}</TicketName>
                  <TicketQuantity>Quantity: {ticket.quantity}</TicketQuantity>
                </TicketInfo>
                <TicketPrice>£{(ticket.price * ticket.quantity).toFixed(2)}</TicketPrice>
              </TicketItem>
            ))}
        </TicketsList>
      </DetailsSection>

      <TotalSection>
        <TotalRow>
          <TotalLabel>Subtotal</TotalLabel>
          <TotalValue>£{totalAmount.toFixed(2)}</TotalValue>
        </TotalRow>
        
        <TotalRow>
          <TotalLabel>VAT (20%)</TotalLabel>
          <TotalValue>£{vatAmount.toFixed(2)}</TotalValue>
        </TotalRow>
        
        <TotalRow>
          <TotalLabel>Total Paid</TotalLabel>
          <TotalValue>£{finalTotal.toFixed(2)}</TotalValue>
        </TotalRow>
      </TotalSection>

      <ActionButtons>
        <SecondaryButton onClick={handleReturnHome}>
          Return Home
        </SecondaryButton>
      </ActionButtons>

      <InfoBox>
        <InfoText>
          Please check your email for your booking confirmation and ticket details. Your tickets will be sent to you shortly.
        </InfoText>
      </InfoBox>
    </ConfirmationContainer>
  );
};

export default BookingConfirmation;
